import { CanAccess } from '@modules/common/components/CanAccess';
import { useIsAuthorized } from '@modules/common/components/CanAccess/hooks';
import { useNotificationContext } from '@modules/common/components/Notification/NotificationContext';
import { formatDateTimeRange, mapToEdgeTimeRange } from '@modules/common/utils';
import {
  getEqlWhereString,
  getSavedTimeRange,
  trackDashboardGlobalFilterEvents,
} from '@modules/dashboard/helpers';
import {
  useGetHasWhereClauseChanged,
  useIsDashboardEditable,
  useUpsertDashboard,
} from '@modules/dashboard/hooks';
import { DashboardActionType } from '@modules/dashboard/store/dashboardActionType';
import { useDashboardStore } from '@modules/dashboard/store/useDashboardStore';
import { LoadingButton } from '@mui/lab';
import { Box, Button, ButtonProps, Stack, Tooltip } from '@mui/material';
import { PermissionOperation, PermissionResource } from 'generated/graphql';
import { isEqual } from 'lodash';
import { useSearchParams } from 'react-router-dom';
import { useGetEQLExpression } from 'v2/core/repository/edge-server/eql/useGetEQLExpression';
import useGetSourceDetailsMap from 'v2/core/repository/edge-server/source-details';
import { mapToEQL } from 'v2/core/repository/eql/mappers';
import { parseEQL } from 'v2/core/repository/eql/parser';
import { ExpressionType } from 'v2/core/repository/eql/types';
import { EmptyWhereClause } from 'v2/core/services/queryBuilder/whereClause';
import { useGetUser } from 'v2/core/services/user';

interface GlobalFilterActionsProps {
  isQueryBuilderVisible: boolean;
  shouldHideSaveToDashboardButton?: boolean;
  buttonColor: ButtonProps['color'];
}

export const GlobalFilterActions = ({
  isQueryBuilderVisible = false,
  shouldHideSaveToDashboardButton = false,
  buttonColor,
}: GlobalFilterActionsProps) => {
  const {
    tenant: { id: tenantID, timezone },
  } = useGetUser();

  const { showSuccessNotification, showErrorNotification } =
    useNotificationContext();
  const [searchParams, setSearchParams] = useSearchParams();

  const dashboard = useDashboardStore(({ dashboard }) => dashboard);
  const dashboardTypeDisplayName = useDashboardStore(
    ({ visual }) => visual.dashboardTypeDisplayName,
  );
  const query = useDashboardStore(({ query }) => query);
  const dispatch = useDashboardStore(({ dispatch }) => dispatch);
  const { upsertDashboard, loading } = useUpsertDashboard();

  const { data: sourceDetailsMap } = useGetSourceDetailsMap();

  const { eql } = useGetEQLExpression({
    tenantID,
    eqlProps: {
      whereClause: query.whereClause,
      expressionType: ExpressionType.Records,
    },
  });

  const onResetFilter = () => {
    if (sourceDetailsMap && dashboard.filterClause?.whereClause) {
      const eqlExpression = `${mapToEQL({
        expressionType: ExpressionType.Records,
        sourceDetailsMap,
      })} where ${dashboard.filterClause.whereClause}`;

      dispatch({
        type: DashboardActionType.SET_QUERY,
        payload: {
          whereClause:
            parseEQL(eqlExpression, sourceDetailsMap, timezone).whereClause ??
            EmptyWhereClause,
        },
      });
    } else {
      dispatch({
        type: DashboardActionType.SET_QUERY,
        payload: {
          whereClause: EmptyWhereClause,
        },
      });
      dispatch({
        type: DashboardActionType.SET_VISUAL,
        payload: {
          shouldShowQueryBuilder: false,
        },
      });
    }

    trackDashboardGlobalFilterEvents({
      eventName: 'Dashboard Global Filter - WhereClause Removed',
    });
  };

  const isEditable = useIsDashboardEditable();

  const isAuthorized = useIsAuthorized({
    operation: PermissionOperation.Write,
    resource: PermissionResource.Artefact,
  });

  const onSave = async () => {
    if (!dashboard || !isEditable) {
      return;
    }
    try {
      const eqlWhereString = getEqlWhereString(eql);
      // Reset if any search parameters are present
      // TODO: Find better solution for it as quick followup
      searchParams.delete('queryParameters');
      setSearchParams(searchParams);
      await upsertDashboard({
        dashboardProps: {
          id: dashboard.id,
          viewToken: dashboard.viewToken,
          editToken: dashboard.editToken ?? '',
          filterClause: {
            timeRange: query.duration
              ? mapToEdgeTimeRange(query.duration)
              : undefined,
            whereClause: eqlWhereString,
          },
        },
      });
      showSuccessNotification({
        contentComponent: `${dashboardTypeDisplayName} updated successfully.`,
        props: { isClosable: true },
      });
      trackDashboardGlobalFilterEvents({
        eventName: 'Dashboard Global Filter - Filters Saved',
        query: {
          duration: query.duration
            ? formatDateTimeRange(query.duration)
            : undefined,
          whereClause: eqlWhereString,
        },
        route: window.location.pathname,
      });
    } catch {
      showErrorNotification({
        contentComponent: 'Failed to update dashboard.',
        props: { isClosable: true },
      });
    }
  };

  const savedTimeRange = getSavedTimeRange({
    timeRange: dashboard?.filterClause?.timeRange,
  });

  const hasWhereClauseChanged = useGetHasWhereClauseChanged({
    whereClause: query.whereClause,
    filterClause: dashboard?.filterClause,
  });
  const hasFiltersToBeSaved =
    !isEqual(query.duration, savedTimeRange) || hasWhereClauseChanged;

  const saveButtonWithoutTooltip = (
    <CanAccess
      operation={PermissionOperation.Write}
      resource={PermissionResource.Artefact}
    >
      <LoadingButton
        disabled={!isEditable || !hasFiltersToBeSaved}
        loading={loading}
        onClick={onSave}
        variant="contained"
        color={buttonColor}
      >
        Save to {dashboardTypeDisplayName}
      </LoadingButton>
    </CanAccess>
  );

  const saveButton =
    !isEditable && isAuthorized ? (
      <Tooltip
        arrow
        title="Ask Dashboard owner to add you as a collaborator to save your filters"
        placement="top"
      >
        <Box>{saveButtonWithoutTooltip}</Box>
      </Tooltip>
    ) : (
      saveButtonWithoutTooltip
    );

  return (
    <Stack spacing={3} direction="row">
      {isQueryBuilderVisible && (
        <Tooltip arrow title="Reset to original version" placement="top">
          <Button
            onClick={onResetFilter}
            variant="outlined"
            disabled={!hasWhereClauseChanged}
            color={buttonColor}
          >
            Reset Filter
          </Button>
        </Tooltip>
      )}
      {!shouldHideSaveToDashboardButton && saveButton}
    </Stack>
  );
};
